import Modal from '../../../components/Modal';
import { AnalysisCharacter, VoiceSwapMatchedSegment, VoiceSwapTask } from '../../../types';
import { tosAccelerate } from '../../../utils/tosAccelerate';

type Translate = (key: string, options?: Record<string, unknown>) => string;

type SwapTaskView = Pick<VoiceSwapTask, 'status' | 'replacedVocalUrl' | 'bgmUrl' | 'errorMessage'> & {
  matchedSegments?: VoiceSwapMatchedSegment[];
};

interface VoiceSwapModalProps {
  open: boolean;
  swapTask: SwapTaskView | null;
  swapCharacters: string[];
  swapLoading: boolean;
  analysisChars: AnalysisCharacter[];
  t: Translate;
  onClose: () => void;
  onReset: () => void;
  onToggleCharacter: (characterName: string) => void;
  onSwapVoice: () => void;
}

function formatTime(ms: number) {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
}

function confidenceColor(confidence: number) {
  if (confidence >= 0.8) return '#16a34a';
  if (confidence >= 0.5) return '#ea580c';
  return '#dc2626';
}

export default function VoiceSwapModal({
  open,
  swapTask,
  swapCharacters,
  swapLoading,
  analysisChars,
  t,
  onClose,
  onReset,
  onToggleCharacter,
  onSwapVoice,
}: VoiceSwapModalProps) {
  const voicedCharacters = analysisChars.filter(character => character.voiceUrl);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('swapModal.title')}
      width={swapTask?.status === 'completed' && swapTask?.matchedSegments?.length ? '640px' : '480px'}
    >
      {swapTask?.status === 'completed' ? (
        <div>
          <p style={{ fontSize: '0.85rem', color: 'var(--success, #16a34a)', marginBottom: '0.75rem', fontWeight: 500 }}>{t('swapModal.completed')}</p>
          {swapTask.replacedVocalUrl && (
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.82rem', color: 'var(--text-2)' }}>{t('swapModal.replacedVocal')}</span>
              <audio src={tosAccelerate(swapTask.replacedVocalUrl)} controls preload="metadata" style={{ height: '32px', display: 'block', marginTop: '0.3rem' }} />
            </div>
          )}
          {swapTask.bgmUrl && (
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.82rem', color: 'var(--text-2)' }}>{t('swapModal.bgm')}</span>
              <audio src={tosAccelerate(swapTask.bgmUrl)} controls preload="metadata" style={{ height: '32px', display: 'block', marginTop: '0.3rem' }} />
            </div>
          )}
          {swapTask.matchedSegments && swapTask.matchedSegments.length > 0 && (
            <div style={{ marginTop: '0.75rem' }}>
              <p style={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--text-2)', marginBottom: '0.5rem' }}>
                {t('swapModal.matchedSegments', { count: swapTask.matchedSegments.length })}
              </p>
              <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-2)', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', fontWeight: 500, borderBottom: '1px solid var(--border)' }}>{t('swapModal.colCharacter')}</th>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', fontWeight: 500, borderBottom: '1px solid var(--border)' }}>{t('swapModal.colTime')}</th>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', fontWeight: 500, borderBottom: '1px solid var(--border)' }}>{t('swapModal.colTranscript')}</th>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', fontWeight: 500, borderBottom: '1px solid var(--border)' }}>{t('swapModal.colScriptLine')}</th>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'center', fontWeight: 500, borderBottom: '1px solid var(--border)' }}>{t('swapModal.colConfidence')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {swapTask.matchedSegments.map((segment, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '0.35rem 0.5rem', fontWeight: 500, whiteSpace: 'nowrap' }}>{segment.character}</td>
                        <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap', color: 'var(--text-3)', fontFamily: 'monospace' }}>
                          {formatTime(segment.start_ms)}-{formatTime(segment.end_ms)}
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={segment.transcript_text}>
                          {segment.transcript_text}
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={segment.script_line}>
                          {segment.script_line}
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center' }}>
                          <span style={{ color: confidenceColor(segment.confidence), fontWeight: 500 }}>{Math.round(segment.confidence * 100)}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', gap: '0.5rem' }}>
            <button className="btn btn-secondary" style={{ fontSize: '0.8rem' }} onClick={onClose}>{t('close', { ns: 'common' })}</button>
            <button className="btn btn-primary" style={{ fontSize: '0.8rem' }} onClick={onReset}>
              {t('swapModal.reSwapVoice')}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-2)', marginBottom: '0.75rem' }}>
            {t('swapModal.selectCharacters')}
          </p>
          {voicedCharacters.length === 0 ? (
            <p style={{ color: 'var(--text-3)', fontSize: '0.82rem' }}>{t('swapModal.noVoiceChars')}</p>
          ) : (
            <>
              {voicedCharacters.map(character => {
                const checked = swapCharacters.includes(character.name);
                return (
                  <label key={character.name} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0', cursor: 'pointer', fontSize: '0.85rem' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggleCharacter(character.name)}
                    />
                    <span style={{ fontWeight: 500 }}>{character.name}</span>
                    <audio src={tosAccelerate(character.voiceUrl || '')} controls preload="metadata" style={{ height: '24px', flex: '0 1 150px' }} />
                  </label>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', gap: '0.5rem' }}>
                <button className="btn btn-secondary" style={{ fontSize: '0.8rem' }} onClick={onClose}>{t('cancel', { ns: 'common' })}</button>
                <button
                  className="btn btn-primary"
                  style={{ fontSize: '0.8rem' }}
                  disabled={swapCharacters.length === 0 || swapLoading}
                  onClick={onSwapVoice}
                >
                  {swapLoading ? t('swapModal.submitting') : t('swapModal.swapCount', { count: swapCharacters.length })}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
